import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { NotificationsPopup } from 'components/Popups';
import { Dropdown } from 'components/Reusable';
import { NotificationStatuses } from 'components/Reusable/NotificationsList/data';
import ToolbarData from 'components/Toolbar/data';

import { useMemoSelector } from 'hooks';
import Api from 'services/Api';
import {
  dispatch,
  getActiveNavItemId,
  getAdditionalRoutes,
  getIsOpenSearch,
  getIsOpenSidebar,
  getIsOpenTopMenu,
  getNotificationsCount,
  getUserIsTeamAdmin,
  getUserRole,
  setNotificationsConfig,
} from 'store';
import {
  ADMIN_USER_ROLES,
  NOTIFICATION_BADGE_UPDATE_INTERVAL,
  NOTIFICATIONS_PAGE_ROUTE_ID,
  Paths,
  SEARCH_PAGE_ROUTE_ID,
} from 'utils';

import { ReactComponent as CrownLogo } from 'assets/crownLogo.svg';
import styles from './Toolbar.module.scss';

const defaultToolId = -1;

const Toolbar = (): JSX.Element => {
  const history = useHistory();

  const {
    isOpenSidebar,
    isOpenTopMenu,
    isOpenSearch,
    activeNavItemId,
    userRole,
    isTeamAdmin,
    additionalRoutes,
    notificationsCount,
  } = useMemoSelector((state) => ({
    isOpenSidebar: getIsOpenSidebar(state),
    isOpenTopMenu: getIsOpenTopMenu(state),
    isOpenSearch: getIsOpenSearch(state),
    activeNavItemId: getActiveNavItemId(state),
    userRole: getUserRole(state),
    isTeamAdmin: getUserIsTeamAdmin(state),
    additionalRoutes: getAdditionalRoutes(state),
    notificationsCount: getNotificationsCount(state),
  }));

  const [activeToolId, setActiveToolId] = useState(defaultToolId);

  const myAccountRouteId = useMemo(
    () => additionalRoutes.find((item) => item.path === Paths.myAccount)?.id || 0,
    [additionalRoutes]
  );

  const updateNotificationsCount = async () => {
    const res = await Api.getNotifications(
      { skip: 0, take: 1, status: NotificationStatuses.new },
      { isNotificationsIntervalCall: true }
    );
    Array.isArray(res?.notifications) && dispatch(setNotificationsConfig({ count: res?.totalCount }));
  };

  useEffect(() => {
    updateNotificationsCount();

    const intervalId = setInterval(updateNotificationsCount, NOTIFICATION_BADGE_UPDATE_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const onClickTool = (e: React.MouseEvent, id: number, cb?: () => void) => {
    if (!e.currentTarget.contains(e.target as HTMLElement)) return;

    setActiveToolId(id === activeToolId ? defaultToolId : id);
    cb && cb();
  };

  useEffect(() => {
    // Note: fixing Search Icon active state, it should reset when going to another route
    setActiveToolId(defaultToolId);
  }, [history.location.pathname]);

  useEffect(() => {
    if (isOpenSearch) return;
    setActiveToolId(defaultToolId);
  }, [isOpenSearch]);

  return (
    <div
      className={classNames(styles.toolbar, {
        [styles.openSidebar]: isOpenSidebar,
        [styles.openTopMenu]: isOpenTopMenu,
      })}
    >
      <div className={styles.crownContainer}>
        <CrownLogo className={styles.crownLogo} />
      </div>
      {ToolbarData.map(({ onClick, id, className, isAdditionalRoutes, Icon }) => (
        <Fragment key={id}>
          {className === 'notificationBtn' ? (
            <NotificationsPopup isOpen={id === activeToolId} handleClose={() => setActiveToolId(defaultToolId)}>
              <button
                className={classNames(styles.toolbarBtn, styles[className as never], {
                  [styles.activeNotification]: notificationsCount > 0,
                  [styles.activeTool]: id === activeToolId || activeNavItemId === NOTIFICATIONS_PAGE_ROUTE_ID,
                  [styles.disabled]: activeNavItemId === NOTIFICATIONS_PAGE_ROUTE_ID,
                })}
                onClick={(e) => onClickTool(e, id, onClick)}
              >
                <Icon />
              </button>
            </NotificationsPopup>
          ) : (
            <button
              className={classNames(styles.toolbarBtn, styles[className as never], {
                [styles.activeTool]:
                  (id === activeToolId && className !== 'burgerBtn') ||
                  (className === 'profileBtn' && additionalRoutes.length && activeNavItemId >= myAccountRouteId) ||
                  (className === 'searchBtn' && activeNavItemId === SEARCH_PAGE_ROUTE_ID),
              })}
              onClick={(e) => onClickTool(e, id, onClick)}
            >
              <Icon />
            </button>
          )}
          {id === activeToolId && isAdditionalRoutes && (
            <Dropdown
              isVisible
              showFooterLinks
              items={additionalRoutes.filter((item) =>
                item.path === Paths.admin ? ADMIN_USER_ROLES.includes(userRole) || isTeamAdmin : true
              )}
              defaultSelectedItemId={activeNavItemId}
              listContainerClassName={styles.dropDownListToolbar}
              onClickItem={(route) => history.push(route.path)}
              onClose={() => setActiveToolId(defaultToolId)}
              selectItem={additionalRoutes && activeNavItemId >= myAccountRouteId}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
};

export default Toolbar;
